import React, { useState } from 'react';
import {
  FunnelIcon,
  ChevronDownIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { MultiSelectFilter, FilterTag, FILTER_CATEGORIES, FailureFilters } from '@/types/filters';
import { FilterTags } from './FilterTags';
import SearchableMultiSelect from './SearchableMultiSelect';

interface FilterSidebarProps {
  filters: FailureFilters;
  onFiltersChange: (newFilters: FailureFilters) => void;
  filterOptions: {
    sites: string[];
    turbineModels: string[];
    componentsMajor: string[];
    componentsMinor: string[];
    failureModes: string[];
    outcomeName: string[];
    excludedCaseIds: string[];
  };
  isLoading?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Sidebar filter panel with collapsible layout
 */
const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onFiltersChange,
  filterOptions,
  isLoading = false,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Generate filter tags for visual display
  const generateFilterTags = (): FilterTag[] => {
    const tags: FilterTag[] = [];

    // Multi-select filters
    Object.entries(FILTER_CATEGORIES).forEach(([key, config]) => {
      const filterKey = config.key;
      const values = filters[filterKey] || [];

      // Special handling for excluded cases - show each case ID individually
      if (key === 'excludedCases') {
        values.forEach((caseId: string) => {
          tags.push({
            id: `excludedCase-${caseId}`,
            category: key as keyof typeof FILTER_CATEGORIES,
            label: `Excluded Case`,
            value: caseId,
            color: config.color, // Use the color from FILTER_CATEGORIES
          });
        });
      } else {
        // Standard multi-select handling
        values.forEach((value: string) => {
          tags.push({
            id: `${key}-${value}`,
            category: key as keyof typeof FILTER_CATEGORIES,
            label: config.label,
            value,
            color: config.color, // Use the color from FILTER_CATEGORIES
          });
        });
      }
    });

    // Numeric range filters
    if (filters.operationTimeMin !== undefined || filters.operationTimeMax !== undefined) {
      const min = filters.operationTimeMin ?? 0;
      const max = filters.operationTimeMax ?? '∞';
      tags.push({
        id: 'operation-time-range',
        category: 'time',
        label: 'Operation Time',
        value: `${min}-${max} years`,
        color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      });
    }

    // Date range filters
    if (filters.dateFrom || filters.dateTo) {
      const from = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : 'Any';
      const to = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : 'Any';
      tags.push({
        id: 'date-range',
        category: 'date',
        label: 'Date Range',
        value: `${from} - ${to}`,
        color: 'bg-gray-100 text-gray-800 border-gray-200',
      });
    }

    return tags;
  };

  const filterTags = generateFilterTags();

  // Expand or collapse all advanced sections
  const toggleAllAdvanced = (expand: boolean) => {
    setShowAdvanced(expand);
  };

  // Toggle dropdown visibility
  const toggleDropdown = (key: string) => {
    const newOpenDropdowns = new Set(openDropdowns);
    if (newOpenDropdowns.has(key)) {
      newOpenDropdowns.delete(key);
    } else {
      newOpenDropdowns.add(key);
    }
    setOpenDropdowns(newOpenDropdowns);
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (
    filterKey: keyof MultiSelectFilter,
    value: string,
    checked: boolean
  ) => {
    const currentValues = filters[filterKey] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v) => v !== value);

    onFiltersChange({
      ...filters,
      [filterKey]: newValues,
      page: 1, // Reset pagination
    });
  };

  // Remove specific filter tag by ID
  const removeFilterTag = (tagId: string) => {
    const tag = filterTags.find(t => t.id === tagId);
    if (!tag) return;
    if (tag.category === 'time') {
      onFiltersChange({
        ...filters,
        operationTimeMin: undefined,
        operationTimeMax: undefined,
        page: 1,
      });
    } else if (tag.category === 'date') {
      onFiltersChange({
        ...filters,
        dateFrom: undefined,
        dateTo: undefined,
        page: 1,
      });
    } else {
      // Multi-select filter - find the category config by matching key
      const categoryKey = tag.category as keyof typeof FILTER_CATEGORIES;
      const categoryConfig = FILTER_CATEGORIES[categoryKey];
      if (categoryConfig) {
        const currentValues = filters[categoryConfig.key] || [];
        const newValues = currentValues.filter((v) => v !== tag.value);
        onFiltersChange({
          ...filters,
          [categoryConfig.key]: newValues,
          page: 1,
        });
      }
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    onFiltersChange({
      sites: [],
      turbineModels: [],
      componentsMajor: [],
      componentsMinor: [],
      outcomeName: [],
      failureModes: [],
      excludedCaseIds: [],
      operationTimeMin: undefined,
      operationTimeMax: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      page: 1,
      size: filters.size,
    });
  };

  // Handle numeric range changes
  const handleNumericRangeChange = (
    key: 'operationTimeMin' | 'operationTimeMax',
    value: string
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value ? Number(value) : undefined,
      page: 1,
    });
  };

  // Handle date range changes
  const handleDateRangeChange = (key: 'dateFrom' | 'dateTo', value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
      page: 1,
    });
  };

  if (isCollapsed) {
    return (
      <div className="bg-white border-r border-gray-200 h-full flex flex-col w-12 relative">
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Expand filters"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <FunnelIcon className="h-6 w-6 text-gray-400" />
          {filterTags.length > 0 && (
            <div className="mt-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              {filterTags.length}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      <div className="lg:hidden fixed inset-0 z-50 flex">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onToggleCollapse} />
        <div className="relative flex flex-col w-full max-w-xs bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              onClick={onToggleCollapse}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Data Filters</h3>
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {filterTags.length} filter{filterTags.length !== 1 ? 's' : ''} applied
                </p>
                <button
                  onClick={() => toggleAllAdvanced(!showAdvanced)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {showAdvanced ? 'Collapse Advanced' : 'Show Advanced'}
                </button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-6">
              {/* Filter Tags */}
              <FilterTags
                tags={filterTags}
                onRemoveTag={removeFilterTag}
                onClearAll={clearAllFilters}
                showClearAll={filterTags.length > 0}
              />

              {/* Filter Controls - Mobile content */}
              <div className="space-y-4">
                {Object.entries(FILTER_CATEGORIES)
                  .filter(([key]) => key !== 'excludedCases')
                  .map(([key, config]) => {
                    const currentValues = filters[config.key] || [];
                    const optionsKey = key === 'failureTypes' ? 'outcomeName' : key;
                    const options = filterOptions[optionsKey as keyof typeof filterOptions] || [];

                    if (key === 'failureModes') {
                      return (
                        <SearchableMultiSelect
                          key={key}
                          label={config.label}
                          description={config.description}
                          options={options}
                          selectedValues={currentValues}
                          onOptionChange={(option, checked) =>
                            handleMultiSelectChange(config.key, option, checked)
                          }
                          onClearAll={() =>
                            onFiltersChange({
                              ...filters,
                              [config.key]: [],
                              page: 1,
                            })
                          }
                          onSelectAll={() =>
                            onFiltersChange({
                              ...filters,
                              [config.key]: options,
                              page: 1
                            })
                          }
                          placeholder={`Search ${config.label.toLowerCase()}...`}
                          isLoading={isLoading}
                        />
                      );
                    }

                    const isOpen = openDropdowns.has(key);

                    return (
                      <div key={key} className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {config.label}
                          <span className="text-gray-400 text-xs ml-1">({options.length})</span>
                        </label>

                        <div className="relative">
                          <button
                            onClick={() => toggleDropdown(key)}
                            disabled={isLoading}
                            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 transition-colors text-sm"
                          >
                            <span className="text-gray-700">
                              {currentValues.length === 0
                                ? `Select ${config.label.toLowerCase()}...`
                                : `${currentValues.length} selected`}
                            </span>
                            <ChevronDownIcon
                              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>

                          {isOpen && (
                            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                              <div className="p-2">
                                <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    {config.description}
                                  </span>
                                  {currentValues.length < options.length && (
                                    <button
                                      onClick={() =>
                                        onFiltersChange({
                                          ...filters,
                                          [config.key]: options,
                                          page: 1
                                        })}
                                      className="text-xs text-blue-600 hover:text-blue-800 onyx-subtitle transition-colors"
                                    >
                                      Select All
                                    </button>
                                  )}
                                  {currentValues.length > 0 && (
                                    <button
                                      onClick={() =>
                                        onFiltersChange({
                                          ...filters,
                                          [config.key]: [],
                                          page: 1,
                                        })
                                      }
                                      className="text-xs text-red-600 hover:text-red-800 font-medium transition-colors"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                {options.map((option) => (
                                  <label
                                    key={option}
                                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={currentValues.includes(option)}
                                      onChange={(e) =>
                                        handleMultiSelectChange(config.key, option, e.target.checked)
                                      }
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 flex-1">
                                      {option}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {/* Advanced Filters Section */}
                {showAdvanced && (
                  <div className="pt-4 border-t border-gray-200 space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Advanced Filters</h4>

                    {/* Operation Time Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Operation Time Range (years)
                      </label>
                      <div className="space-y-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.operationTimeMin || ''}
                          onChange={(e) => handleNumericRangeChange('operationTimeMin', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          step="0.1"
                          min="0"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.operationTimeMax || ''}
                          onChange={(e) => handleNumericRangeChange('operationTimeMax', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          step="0.1"
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Date Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Failure Date Range
                      </label>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={filters.dateFrom || ''}
                          onChange={(e) => handleDateRangeChange('dateFrom', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <input
                          type="date"
                          value={filters.dateTo || ''}
                          onChange={(e) => handleDateRangeChange('dateTo', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex bg-white border-r border-gray-200 h-full flex-col w-80">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Data Filters</h3>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <button
                onClick={onToggleCollapse}
                className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                title="Collapse sidebar"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filterTags.length} filter{filterTags.length !== 1 ? 's' : ''} applied
            </p>
            <button
              onClick={() => toggleAllAdvanced(!showAdvanced)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              {showAdvanced ? 'Collapse Advanced' : 'Show Advanced'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-visible px-6 py-4 space-y-6">
          {/* Filter Tags */}
          <FilterTags
            tags={filterTags}
            onRemoveTag={removeFilterTag}
            onClearAll={clearAllFilters}
            showClearAll={filterTags.length > 0}
          />

          {/* Filter Controls */}
          <div className="space-y-4">
            {/* Multi-Select Dropdowns */}
            {Object.entries(FILTER_CATEGORIES)
              .filter(([key]) => key !== 'excludedCases') // Exclude from dropdowns - managed via table checkboxes
              .map(([key, config]) => {
                const currentValues = filters[config.key] || [];
                // For failureTypes category, we need to look at outcomeName in filterOptions
                const optionsKey = key === 'failureTypes' ? 'outcomeName' : key;
                const options = filterOptions[optionsKey as keyof typeof filterOptions] || [];

                // Use SearchableMultiSelect for failure modes
                if (key === 'failureModes') {
                  return (
                    <SearchableMultiSelect
                      key={key}
                      label={config.label}
                      description={config.description}
                      options={options}
                      selectedValues={currentValues}
                      onOptionChange={(option, checked) =>
                        handleMultiSelectChange(config.key, option, checked)
                      }
                      onClearAll={() =>
                        onFiltersChange({
                          ...filters,
                          [config.key]: [],
                          page: 1,
                        })
                      }
                      onSelectAll={() =>
                        onFiltersChange({
                          ...filters,
                          [config.key]: options,
                          page: 1
                        })
                      }
                      placeholder={`Search ${config.label.toLowerCase()}...`}
                      isLoading={isLoading}
                    />
                  );
                }

                // Regular multi-select dropdown for other filters
                const isOpen = openDropdowns.has(key);

                return (
                  <div key={key} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {config.label}
                      <span className="text-gray-400 text-xs ml-1">({options.length})</span>
                    </label>

                    <div className="relative">
                      <button
                        onClick={() => toggleDropdown(key)}
                        disabled={isLoading}
                        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 transition-colors text-sm"
                      >
                        <span className="text-gray-700">
                          {currentValues.length === 0
                            ? `Select ${config.label.toLowerCase()}...`
                            : `${currentValues.length} selected`}
                        </span>
                        <ChevronDownIcon
                          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {isOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div className="p-2">
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                              <span className="text-xs font-medium text-gray-500 uppercase">
                                {config.description}
                              </span>
                              {currentValues.length < options.length && (
                                <button
                                  onClick={() =>
                                    onFiltersChange({
                                      ...filters,
                                      [config.key]: options,
                                      page: 1
                                    })}
                                  className="text-xs text-blue-600 hover:text-blue-800 onyx-subtitle transition-colors"
                                >
                                  Select All
                                </button>
                              )}
                              {currentValues.length > 0 && (
                                <button
                                  onClick={() =>
                                    onFiltersChange({
                                      ...filters,
                                      [config.key]: [],
                                      page: 1,
                                    })
                                  }
                                  className="text-xs text-red-600 hover:text-red-800 font-medium transition-colors"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {options.map((option) => (
                              <label
                                key={option}
                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={currentValues.includes(option)}
                                  onChange={(e) =>
                                    handleMultiSelectChange(config.key, option, e.target.checked)
                                  }
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 flex-1">
                                  {option}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            {/* Advanced Filters Toggle */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Advanced Filters
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                />
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  {/* Operation Time Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operation Time Range (years)
                    </label>
                    <div className="space-y-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.operationTimeMin || ''}
                        onChange={(e) => handleNumericRangeChange('operationTimeMin', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        step="0.1"
                        min="0"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.operationTimeMax || ''}
                        onChange={(e) => handleNumericRangeChange('operationTimeMax', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        step="0.1"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Failure Date Range
                    </label>
                    <div className="space-y-2">
                      <input
                        type="date"
                        value={filters.dateFrom || ''}
                        onChange={(e) => handleDateRangeChange('dateFrom', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <input
                        type="date"
                        value={filters.dateTo || ''}
                        onChange={(e) => handleDateRangeChange('dateTo', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;